import { useInView } from "react-intersection-observer";
import {
  layout,
  LayoutProps,
  space,
  SpaceProps,
  border,
  BorderProps,
  FlexboxProps,
  flexbox,
} from "styled-system";
import styled, { keyframes } from "styled-components";

import useNativeLazyLoading from "../NativeLazyLoadingHook/useNativeLazyLoading";

import { LazyImageProps } from "./image.type";

const loading = keyframes`
  0% { background-position-x: 0; }
  40%, 100% { background-position-x: -200%; }
`;

type PlaceholderProps = LayoutProps &
  SpaceProps &
  FlexboxProps &
  BorderProps & { css?: string };

const Placeholder = styled.div<PlaceholderProps>`
  display: flex;
  box-sizing: border-box;
  overflow: hidden;
  background-size: 200% 100%;
  background-position: 0 center;
  animation: 2s ease-in-out ${loading} infinite;
  ${layout}
  ${space}
  ${border}
  ${flexbox}
  ${(props) => props.css}
`;

const Img = styled.img<LayoutProps>`
  height: 100%;
  object-fit: cover;
  ${layout}
`;

const LazyImage = ({
  height = "100px",
  width = "100%",
  sizes = "100vw",
  className,
  borderRadius,
  css,
  marginX,
  marginY,
  paddingX,
  paddingY,
  shouldRespectImageWidth = false,
  justifyContent = "center",
  ...props
}: LazyImageProps) => {
  const supportsLazyLoading = useNativeLazyLoading();
  const [ref, inView] = useInView({
    // once the image is loaded we should not execute the code anymore
    triggerOnce: true,
    // amount of pixels by which the image is under the viewport before it's fetched
    // intersection observer API
    rootMargin: "200px 0px",
  });

  return (
    <Placeholder
      ref={!supportsLazyLoading ? ref : undefined}
      width={width}
      height={height}
      className={className}
      borderRadius={borderRadius}
      css={css}
      mx={marginX}
      my={marginY}
      px={paddingX}
      py={paddingY}
      justifyContent={justifyContent}
      data-testid="lazy-image-container"
    >
      {inView || supportsLazyLoading ? (
        <Img
          width={shouldRespectImageWidth ? "auto" : "100%"}
          {...props}
          sizes={sizes}
        />
      ) : null}
    </Placeholder>
  );
};

export default LazyImage;
